import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, PlayIcon, PencilIcon, PauseIcon, TrashIcon } from '@heroicons/react/24/outline';
import { chatbotApi, userApi, modelsApi, discordApi, wordpressApi } from '../services/api';
import { Chatbot, User, Model } from '../types';
import { toast } from 'react-hot-toast';
import { getAndClearIntegrationData, showIntegrationError, showIntegrationSuccess } from '../utils/integration';

export default function Dashboard() {
  const [chatbots, setChatbots] = useState<Chatbot[]>([]);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
    checkForIntegrationParams();
  }, []);

  const checkForIntegrationParams = async () => {
    const integrationData = getAndClearIntegrationData();

    if (integrationData) {
      // Extract return_url from cookie data into sessionStorage (unauthenticated flow)
      if (integrationData.source === 'wordpress' && integrationData.return_url) {
        sessionStorage.setItem('wordpress_return_url', integrationData.return_url);
        console.log('WordPress return URL stored from cookie:', integrationData.return_url);
      }
      console.log('Integration detected:', integrationData);
      await processIntegration(integrationData);
    }
  };

  const processIntegration = async (integrationData: { source: string; id: string; timestamp: number; serverName?: string; site_url?: string; site_name?: string }) => {
    try {
      const { source, id, serverName } = integrationData;

      if (source === 'discord') {
        await processDiscordIntegration(id, serverName);
      } else if (source === 'wordpress') {
        await processWordPressIntegration(id, integrationData.site_url, integrationData.site_name);
      } else {
        showIntegrationError(source as any, `Unknown integration source: ${source}`);
      }
    } catch (error) {
      console.error('Error processing integration:', error);
      showIntegrationError(integrationData.source as any, 'Failed to process integration');
    }
  };

  const processDiscordIntegration = async (serverId: string, serverName?: string) => {
    try {
      toast.loading('Processing Discord integration...', { id: 'discord-integration' });

      // Step 1: Check if server is already integrated by this user
      const existingIntegrations = await discordApi.getDiscordIntegrations();
      if (existingIntegrations.success && existingIntegrations.data) {
        const existing = existingIntegrations.data.integrations.find(i => i.server_id === serverId);
        if (existing) {
          // Server is already integrated by this user - show success message
          toast.success('✅ Discord server is already integrated!', { 
            id: 'discord-integration',
            duration: 3000 
          });
          return;
        }
      }

      // Step 2: Check if user has existing chatbots
      // Note: Server access verification will be done by the backend during integration creation
      const existingChatbots = chatbots.filter(bot => bot.status === 'active');
      
      // Create server object with real name from webhook (if available)
      const server = { 
        id: serverId, 
        name: serverName ? decodeURIComponent(serverName) : 'Discord Server' 
      };
      
      if (existingChatbots.length === 0) {
        // New user - auto-create chatbot
        await createChatbotAndIntegration(server, null);
      } else {
        // Existing user - show selection modal
        await showChatbotSelectionModal(server, existingChatbots);
      }

    } catch (error) {
      console.error('Discord integration error:', error);
      showIntegrationError('discord', 'Failed to process Discord integration');
    }
  };

  const processWordPressIntegration = async (siteId: string, siteUrl?: string, siteName?: string) => {
    try {
      toast.loading('Processing WordPress integration...', { id: 'wordpress-integration' });

      // Check if user has existing chatbots
      const existingChatbots = chatbots.filter(bot => bot.status === 'active');

      // Create site object with provided data or defaults
      const site = {
        id: siteId,
        url: siteUrl || 'WordPress Site',
        name: siteName || siteUrl || 'WordPress Site'
      };

      if (existingChatbots.length === 0) {
        // New user - auto-create chatbot
        await createWordPressChatbotAndIntegration(site, null);
      } else {
        // Existing user - show selection modal
        await showWordPressChatbotSelectionModal(site, existingChatbots);
      }

    } catch (error) {
      console.error('WordPress integration error:', error);
      showIntegrationError('wordpress', 'Failed to process WordPress integration');
    }
  };

  const sanitizeServerName = (serverName: string): string => {
    // Remove special characters, limit length, handle empty names
    let sanitized = serverName
      .replace(/[^a-zA-Z0-9\s-_]/g, '') // Remove special chars except spaces, hyphens, underscores
      .trim()
      .substring(0, 50); // Limit to 50 characters
    
    if (!sanitized) {
      // Generate fallback name if sanitization results in empty string
      const randomNum = Math.floor(Math.random() * 9999) + 1;
      sanitized = `Discord Bot ${randomNum}`;
    }
    
    return sanitized;
  };

  const createChatbotAndIntegration = async (server: { id: string; name: string }, selectedChatbotId: string | null) => {
    try {
      let chatbotId = selectedChatbotId;
      
      if (!chatbotId) {
        // Create new chatbot with sanitized server name
        const sanitizedName = sanitizeServerName(server.name);
        
        const createResponse = await chatbotApi.createChatbot({
          name: sanitizedName,
          description: `Discord bot for ${server.name}`,
          // Add any default settings for Discord bots
        });

        if (!createResponse.success || !createResponse.data) {
          throw new Error('Failed to create chatbot');
        }

        chatbotId = createResponse.data.chatbotId;
        
        // Refresh chatbots list to include the new one
        await loadData();
      }

      // Create Discord integration
      const integrationResponse = await discordApi.createDiscordIntegration(
        server.id,
        chatbotId,
        server.name
      );

      if (!integrationResponse.success) {
        throw new Error('Failed to create Discord integration');
      }

      const successMessage = selectedChatbotId 
        ? `Successfully linked ${server.name} to your chatbot!`
        : `Successfully created and linked chatbot "${sanitizeServerName(server.name)}" to ${server.name}!`;
      
      showIntegrationSuccess('discord', successMessage);

    } catch (error) {
      console.error('Error creating chatbot and integration:', error);
      showIntegrationError('discord', 'Failed to create chatbot integration');
    }
  };

  const showChatbotSelectionModal = async (server: { id: string; name: string }, existingChatbots: Chatbot[]) => {
    // For now, auto-create new chatbot by default
    // TODO: Implement proper modal UI for chatbot selection
    
    // Auto-create new chatbot for Discord integration
    // This provides the best user experience for the webhook flow
    toast.loading(`Creating new chatbot for "${server.name}"...`, { id: 'chatbot-creation' });
    
    await createChatbotAndIntegration(server, null);
  };

  // WordPress helper functions
  const createWordPressChatbotAndIntegration = async (site: { id: string; url: string; name: string }, selectedChatbotId?: string | null) => {
    try {
      let chatbotId = selectedChatbotId;
      let sanitizedName = '';

      if (!chatbotId) {
        // Create new chatbot with site name
        sanitizedName = sanitizeServerName(site.name); // Reuse the sanitize function

        const createResponse = await chatbotApi.createChatbot({
          name: sanitizedName,
          description: `WordPress chatbot for ${site.url}`,
        });

        if (!createResponse.success || !createResponse.data) {
          throw new Error('Failed to create chatbot');
        }

        chatbotId = createResponse.data.chatbotId;

        // Refresh chatbots list to include the new one
        await loadData();
      }

      const successMessage = selectedChatbotId
        ? `Successfully linked ${site.name} to your chatbot!`
        : `Successfully created chatbot "${sanitizedName}" for ${site.name}!`;

      showIntegrationSuccess('wordpress', successMessage);

      // Redirect back to WordPress if return URL is available
      const returnUrl = sessionStorage.getItem('wordpress_return_url');
      if (returnUrl) {
        sessionStorage.removeItem('wordpress_return_url');

        // Construct return URL with chatbot_id
        const url = new URL(returnUrl);
        url.searchParams.set('chatbot_id', chatbotId);
        url.searchParams.set('auto_connected', '1');

        console.log('Redirecting back to WordPress:', url.toString());

        // Small delay to show the success message before redirect
        setTimeout(() => {
          window.location.href = url.toString();
        }, 2000);
      }

    } catch (error) {
      console.error('Error creating WordPress chatbot:', error);
      showIntegrationError('wordpress', 'Failed to create WordPress chatbot');
    }
  };

  const showWordPressChatbotSelectionModal = async (site: { id: string; url: string; name: string }, existingChatbots: Chatbot[]) => {
    // For now, auto-create new chatbot by default (same as Discord)
    // TODO: Implement proper modal UI for chatbot selection

    // Auto-create new chatbot for WordPress integration
    toast.loading(`Creating new chatbot for "${site.name}"...`, { id: 'chatbot-creation' });

    await createWordPressChatbotAndIntegration(site, null);
  };

  // Polling effect for chatbots being created
  useEffect(() => {
    const hasCreatingChatbots = chatbots.some(bot => bot.status === 'creating');

    if (!hasCreatingChatbots) {
      return; // No polling needed
    }

    // Start polling
    const interval = setInterval(async () => {
      try {
        const response = await chatbotApi.getChatbots();
        if (response.success && response.data) {
          setChatbots(response.data);
        }
      } catch (error) {
        console.error('Error polling chatbot status:', error);
      }
    }, 2000); // Poll every 2 seconds

    return () => {
      clearInterval(interval);
    };
  }, [chatbots]);

  const getModelAlias = (modelId: string) => {
    const model = models.find(m => m.id === modelId);
    return model?.name || modelId;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [chatbotsResponse, profileResponse, modelsResponse] = await Promise.all([
        chatbotApi.getChatbots().catch(err => {
          console.error('Failed to load chatbots:', err);
          return { success: false as const, error: 'Failed to load chatbots' };
        }),
        userApi.getProfile().catch(err => {
          console.error('Failed to load profile:', err);
          return { success: false as const, error: 'Failed to load profile' };
        }),
        modelsApi.getModels().catch(err => {
          console.error('Failed to load models:', err);
          return { success: false as const, error: 'Failed to load models' };
        })
      ]);

      if (chatbotsResponse.success && 'data' in chatbotsResponse) {
        setChatbots(chatbotsResponse.data || []);
      } else {
        console.warn('Chatbots API failed:', 'error' in chatbotsResponse ? chatbotsResponse.error : 'Unknown error');
      }

      if (profileResponse.success && 'data' in profileResponse) {
        setUserProfile(profileResponse.data);
      } else {
        console.warn('Profile API failed:', 'error' in profileResponse ? profileResponse.error : 'Unknown error');
      }

      if (modelsResponse.success && 'data' in modelsResponse) {
        setModels(modelsResponse.data || []);
      } else {
        console.warn('Models API failed:', 'error' in modelsResponse ? modelsResponse.error : 'Unknown error');
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChatbot = async (chatbot: Chatbot, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click navigation
    try {
      const response = await chatbotApi.updateChatbot(chatbot.chatbotId, {
        suspend: !chatbot.suspend
      });
      if (response.success) {
        setChatbots(prev => prev.map(bot => 
          bot.chatbotId === chatbot.chatbotId 
            ? { ...bot, suspend: !bot.suspend }
            : bot
        ));
        toast.success(chatbot.suspend ? 'Chatbot activated' : 'Chatbot paused');
      } else {
        toast.error('Failed to update chatbot');
      }
    } catch (error) {
      console.error('Error updating chatbot:', error);
      toast.error('Failed to update chatbot');
    }
  };

  const handleDeleteChatbot = async (chatbot: Chatbot, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click navigation
    if (!window.confirm(`Are you sure you want to delete "${chatbot.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await chatbotApi.deleteChatbot(chatbot.chatbotId);
      if (response.success) {
        setChatbots(prev => prev.filter(bot => bot.chatbotId !== chatbot.chatbotId));
        toast.success('Chatbot deleted successfully');
      } else {
        toast.error('Failed to delete chatbot');
      }
    } catch (error) {
      console.error('Error deleting chatbot:', error);
      toast.error('Failed to delete chatbot');
    }
  };

  const handleEditChatbot = (chatbot: Chatbot, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click navigation
    navigate(`/chatbots/${chatbot.chatbotId}/edit`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-6 h-6 border border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Chatbot Cards */}
      {chatbots.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Link
            to="/chatbots/new"
            className="group bg-white/50 dark:bg-slate-800/50 border-2 border-dashed border-gray-300 dark:border-slate-700 rounded-2xl p-6 hover:border-gray-400 dark:hover:border-slate-600 hover:bg-white dark:hover:bg-slate-800 transition-all cursor-pointer flex flex-col items-center justify-center min-h-[200px]"
          >
            <PlusIcon className="w-10 h-10 text-gray-300 dark:text-gray-600 group-hover:text-gray-400 dark:group-hover:text-gray-500 transition-colors mb-3" />
            <h3 className="text-lg font-semibold text-gray-400 dark:text-gray-500 mb-1">
              New Chatbot
            </h3>
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Create your first AI assistant
            </p>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {chatbots.map((chatbot) => {
            const isCreating = chatbot.status === 'creating';
            const isFailed = chatbot.status === 'failed';
            const isPaused = chatbot.suspend;

            // Real indicator data from API
            const hasDiscord = (chatbot.discordServerCount || 0) > 0;
            const activeSessionCount = chatbot.activeSessionCount || 0;
            const hasLiveTraffic = activeSessionCount > 0;
            // Documents OR crawled websites count as knowledge — the badge
            // means "this bot has something to answer from", regardless of source.
            const hasKnowledgeBase = (chatbot.documentCount || 0) > 0 || (chatbot.websiteCount || 0) > 0;

            return (
              <div
                key={chatbot.chatbotId}
                onClick={() => !(isCreating || isFailed) && navigate(`/chatbots/${chatbot.chatbotId}`)}
                className={`group bg-white dark:bg-slate-800 rounded-2xl p-6 border border-gray-200 dark:border-slate-700 shadow-sm relative min-h-[200px] flex flex-col ${
                  isCreating || isFailed ? 'opacity-60' : 'cursor-pointer hover:shadow-md hover:border-gray-300 dark:hover:border-slate-600 transition-all'
                }`}
              >
                {/* Status indicator */}
                <div className="flex items-center gap-2">
                  {isCreating ? (
                    <span className="w-3 h-3 border border-gray-400 border-t-gray-600 rounded-full animate-spin"></span>
                  ) : isFailed ? (
                    <span className="w-2 h-2 rounded-full bg-red-500"></span>
                  ) : isPaused ? (
                    <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                  ) : hasLiveTraffic ? (
                    <span className="relative inline-flex items-center justify-center w-4 h-4">
                      {/* Pulsing outer ring */}
                      <span className="absolute w-4 h-4 rounded-full bg-emerald-400 opacity-40 animate-ping"></span>
                      {/* Pulsing inner ring */}
                      <span className="absolute w-3 h-3 rounded-full bg-emerald-400 opacity-50 animate-ping [animation-delay:150ms]"></span>
                      {/* Center dot */}
                      <span className="relative w-2 h-2 rounded-full bg-emerald-500"></span>
                    </span>
                  ) : (
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  )}
                  <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {isCreating ? 'Setting up' :
                     isFailed ? 'Failed' :
                     isPaused ? 'Paused' :
                     hasLiveTraffic ? `Active [${activeSessionCount}]` :
                     'Online'}
                  </span>
                </div>

                {/* Content */}
                <div className={`flex-1 flex flex-col ${isCreating ? 'pt-4' : 'pt-8'}`}>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 capitalize text-center">
                    {chatbot.name}
                  </h3>
                  {/* Feature indicators - under bot name */}
                  {!isCreating && !isFailed && (hasDiscord || hasKnowledgeBase) && (
                    <div className="flex items-center justify-center gap-3 mt-1">
                      {hasDiscord && (
                        <span className="flex items-center gap-1 text-indigo-400">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.195.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                          </svg>
                          <span className="text-[10px] uppercase tracking-wide">Discord</span>
                        </span>
                      )}
                      {hasKnowledgeBase && (
                        <span className="flex items-center gap-1 text-emerald-500">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
                          </svg>
                          <span className="text-[10px] uppercase tracking-wide">Knowledge</span>
                        </span>
                      )}
                    </div>
                  )}
                  {isCreating && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                      Just a few seconds...
                    </p>
                  )}
                </div>

                {/* Actions */}
                {!isCreating && (
                  <div className="absolute bottom-4 right-6 flex items-center gap-2">
                    {!isFailed && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/chatbots/${chatbot.chatbotId}`); }}
                          className="p-2 text-gray-700 dark:text-gray-300 rounded-full border border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500 hover:text-gray-900 dark:hover:text-white hover:shadow-sm transition-all"
                          title="Edit"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => handleToggleChatbot(chatbot, e)}
                          className="p-2 text-gray-700 dark:text-gray-300 rounded-full border border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500 hover:text-gray-900 dark:hover:text-white hover:shadow-sm transition-all"
                          title={isPaused ? 'Resume' : 'Pause'}
                        >
                          {isPaused ? <PlayIcon className="w-4 h-4" /> : <PauseIcon className="w-4 h-4" />}
                        </button>
                      </>
                    )}
                    <button
                      onClick={(e) => handleDeleteChatbot(chatbot, e)}
                      className="p-2 text-gray-700 dark:text-gray-300 rounded-full border border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500 hover:text-gray-900 dark:hover:text-white hover:shadow-sm transition-all"
                      title="Delete"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}

        </div>
      )}
    </div>
  );
}